import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ExternalLink, LogOut, Menu, Search } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ErrorBoundary } from "@/planner/components/ErrorBoundary";
import { NAV, crumbFor } from "@/app/nav";
import { PUBLIC_SITE, SessionProvider, useSessionState } from "@/app/session";
import SignIn from "@/app/SignIn";
import SearchDialog from "@/app/SearchDialog";
import Home from "@/app/pages/Home";
import Pipeline from "@/app/pages/Pipeline";
import FeedbackInbox from "@/app/pages/FeedbackInbox";
import Locations from "@/app/pages/Locations";
import Visits from "@/app/pages/Visits";
import Outreach from "@/app/pages/Outreach";
import Flyers from "@/app/pages/Flyers";
import Security from "@/app/pages/Security";
import Expenses from "@/app/pages/Expenses";
import ProfitShare from "@/app/pages/ProfitShare";
import AdminCrm from "@/pages/AdminCrm";
import AdminService from "@/pages/AdminService";
import Planner from "@/planner/Planner";
import Sourcing from "@/pages/Sourcing";

const SideNav = ({ onNavigate }) => (
  <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-5" aria-label="Workspace" data-testid="admin-nav" data-lenis-prevent>
    {NAV.map((group, gi) => (
      <div key={group.title || gi}>
        {group.title && (
          <p className="px-3 pb-2 font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">{group.title}</p>
        )}
        <ul className="space-y-0.5">
          {group.items.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink to={to} end={end !== false} onClick={onNavigate}
                className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive ? "bg-vortex-cyan/10 font-medium text-vortex-cyan" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
                <Icon size={16} className="shrink-0" />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    ))}
  </nav>
);

const Brand = () => (
  <Link to="/admin" className="flex items-center gap-3 px-6 py-5">
    <img src="/brand/logo-light.png" alt="Vortex Vending" className="h-7 w-auto" />
  </Link>
);

// The private operator workspace: one sidebar menu, a top bar with search, and every section
// (sales, customers, stock) behind one sign-in. Served at /admin/* and at crm.vortexvs.com.
const AdminApp = () => {
  const session = useSessionState();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // The CRM used to be a tab at /admin#crm.
  useEffect(() => { if (window.location.hash === "#crm") window.location.replace("/admin/crm"); }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!session.token) {
    return <SignIn onSignedIn={session.setToken} />;
  }

  const sideClasses = "flex h-full flex-col border-r border-white/10 bg-vortex-surface";

  return (
    <SessionProvider value={session}>
      <div className="min-h-screen lg:pl-64" data-testid="admin-app">
        <aside className={`${sideClasses} fixed inset-y-0 left-0 z-30 hidden w-64 lg:flex`}>
          <Brand />
          <SideNav />
          <div className="border-t border-white/10 p-3">
            <a href={PUBLIC_SITE} target="_blank" rel="noreferrer"
              className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-slate-400 hover:bg-white/5 hover:text-white">
              <ExternalLink size={16} /> Public website
            </a>
            <button onClick={session.signOut} data-testid="admin-logout-button"
              className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-slate-400 hover:bg-red-500/10 hover:text-red-300">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </aside>

        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetContent side="left" className={`${sideClasses} w-72 p-0 text-slate-100`}>
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <SheetDescription className="sr-only">Workspace sections</SheetDescription>
            <Brand />
            <SideNav onNavigate={() => setMenuOpen(false)} />
            <div className="border-t border-white/10 p-3">
              <button onClick={session.signOut}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-slate-400 hover:text-red-300">
                <LogOut size={16} /> Sign out
              </button>
            </div>
          </SheetContent>
        </Sheet>

        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-white/10 bg-vortex-ink/90 px-4 backdrop-blur-xl sm:px-6">
          <button onClick={() => setMenuOpen(true)} className="rounded-md p-2 text-slate-300 hover:bg-white/5 lg:hidden" aria-label="Open menu">
            <Menu size={20} />
          </button>
          <p className="min-w-0 truncate text-sm text-slate-400" data-testid="admin-breadcrumb">
            {crumbFor(pathname).map((c, i, all) => (
              <span key={c} className={i === all.length - 1 ? "font-medium text-slate-100" : ""}>
                {c}{i < all.length - 1 && <span className="px-2 text-slate-600">/</span>}
              </span>
            ))}
          </p>
          <button onClick={() => setSearchOpen(true)} data-testid="app-search-button"
            className="ml-auto flex items-center gap-2 rounded-md border border-white/10 bg-vortex-surface/60 px-3 py-2 text-sm text-slate-400 transition-colors hover:border-vortex-cyan/50 hover:text-slate-200 sm:w-72">
            <Search size={15} />
            <span className="hidden sm:inline">Search everything</span>
            <kbd className="ml-auto hidden rounded border border-white/10 px-1.5 font-mono text-[10px] text-slate-500 sm:inline">Ctrl K</kbd>
          </button>
        </header>

        <main className="px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <ErrorBoundary key={pathname}>
            <Routes>
              <Route index element={<Home />} />
              <Route path="pipeline" element={<Pipeline />} />
              <Route path="crm" element={<AdminCrm />} />
              <Route path="visits" element={<Visits />} />
              <Route path="outreach" element={<Outreach />} />
              <Route path="flyers" element={<Flyers />} />
              <Route path="service" element={<AdminService />} />
              <Route path="feedback" element={<FeedbackInbox />} />
              <Route path="locations" element={<Locations />} />
              <Route path="planner/*" element={<Planner />} />
              <Route path="sourcing" element={<Sourcing />} />
              <Route path="expenses" element={<Expenses />} />
              <Route path="profit-share" element={<ProfitShare />} />
              <Route path="security" element={<Security />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </ErrorBoundary>
        </main>
        <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      </div>
    </SessionProvider>
  );
};

export default AdminApp;
